/**
 * ================================================================
 * INITIALIZATION COMPONENT: FinancialYearInitializer
 * ================================================================
 *
 * @component        FinancialYearInitializer
 * @description      Handles financial year initialization from localStorage
 * @category         Utility Component
 *
 * @props
 * - children: React.ReactNode - Child components to render
 *
 * @sideEffects
 * - Initializes financial year state from localStorage on mount
 * - Dispatches initialization action to Redux store
 *
 * @usage
 * ```tsx
 * <FinancialYearInitializer>
 *   <App />
 * </FinancialYearInitializer>
 * ```
 * ================================================================
 */

import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';

import { useFinancialYear } from '@/hooks/useFinancialYear';

// ================================================================
// COMPONENT INTERFACE
// ================================================================

/**
 * Props for FinancialYearInitializer component
 *
 * @interface FinancialYearInitializerProps
 * @property {ReactNode} children - Child components to render
 */
interface FinancialYearInitializerProps {
  /** Child components to render after initialization */
  children: ReactNode;
}

// ================================================================
// COMPONENT IMPLEMENTATION
// ================================================================

/**
 * Financial Year Initializer Component
 * Handles financial year state initialization on app startup
 *
 * @component FinancialYearInitializer
 * @param {FinancialYearInitializerProps} props - Component props
 * @returns {JSX.Element} Renders children after initialization
 */
const FinancialYearInitializer: FC<FinancialYearInitializerProps> = ({ children }) => {
  // ================================================================
  // HOOKS
  // ================================================================

  const { initializeFromStorage } = useFinancialYear();

  // ================================================================
  // INITIALIZATION EFFECT
  // ================================================================

  /**
   * Initialize financial year from localStorage on component mount
   * This ensures that user's previous financial year selection is restored
   */
  useEffect(() => {
    initializeFromStorage();
  }, [initializeFromStorage]);

  // ================================================================
  // RENDER
  // ================================================================

  // Simply render children - initialization happens in effect
  return <>{children}</>;
};

// Set display name for debugging
FinancialYearInitializer.displayName = 'FinancialYearInitializer';

// ================================================================
// EXPORTS
// ================================================================

export { FinancialYearInitializer };
export type { FinancialYearInitializerProps };

/**
 * ================================================================
 * END OF COMPONENT: FinancialYearInitializer
 * ================================================================
 *
 * @summary          Handles financial year initialization from localStorage
 * @exports          FinancialYearInitializer component and props interface
 * @dependencies     React hooks, useFinancialYear hook
 * @usedBy           App.tsx for application-wide initialization
 *
 * @sideEffects
 * - Dispatches Redux action to initialize financial year
 * - Reads from localStorage to restore user preference
 * - No visual side effects (transparent to user)
 *
 * ================================================================
 */
